/**
 * Format an ISO date string in the project's timezone.
 * Falls back to America/New_York if no timezone is provided.
 *
 * Usage:
 *   import { formatDate, formatTime, formatDateTime } from '../utils/formatDate';
 *   formatDate(iso, project?.timezone)   → "Mar 24, 2026"
 *   formatTime(iso, project?.timezone)   → "8:32 PM"
 *   formatDateTime(iso, project?.timezone) → "Mar 24, 8:32 PM"
 *   timeAgo(iso) → "5 min ago"
 */

const DEFAULT_TZ = 'America/New_York';

/**
 * Resolve timezone: user preference > project timezone > default.
 * User preference stored in localStorage so each browser remembers.
 */
export function getEffectiveTZ(projectTZ) {
  try {
    return localStorage.getItem('userTimezone') || projectTZ || DEFAULT_TZ;
  } catch {
    return projectTZ || DEFAULT_TZ;
  }
}

export function setUserTimezone(tz) {
  try { localStorage.setItem('userTimezone', tz); } catch {}
}

export function getUserTimezone() {
  try { return localStorage.getItem('userTimezone') || ''; } catch { return ''; }
}

/** Ensure ISO string is treated as UTC if no timezone suffix present. */
function asUTC(iso) {
  if (!iso) return iso;
  const s = String(iso);
  // Already has timezone: Z, +HH:MM, -HH:MM
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) return s;
  return s + 'Z';
}

export function formatDate(iso, tz) {
  if (!iso) return '—';
  try {
    return new Date(asUTC(iso)).toLocaleDateString('en-US', {
      timeZone: getEffectiveTZ(tz),
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return new Date(iso).toLocaleDateString();
  }
}

export function formatTime(iso, tz) {
  if (!iso) return '—';
  try {
    return new Date(asUTC(iso)).toLocaleTimeString('en-US', {
      timeZone: getEffectiveTZ(tz),
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return new Date(iso).toLocaleTimeString();
  }
}

export function formatDateTime(iso, tz) {
  if (!iso) return '—';
  try {
    return new Date(asUTC(iso)).toLocaleString('en-US', {
      timeZone: getEffectiveTZ(tz),
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return new Date(iso).toLocaleString();
  }
}

export function timeAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(asUTC(iso)).getTime();
  if (ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}
